import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import type { ValueGroup } from './valueGroups'
import { useMeasuredCanvas } from '../views/useMeasuredCanvas'

/**
 * SEN-205 — the one layout for a set-valued cell. Owner: "compact the content
 * but never discard it". EVERY group is always rendered (biggest first); when
 * width runs out the cell compacts progressively, inside its own row height
 * and column width:
 *  (a) identical values are already one group with an exact count;
 *  (b) chips shrink to their mark + count — the label stays in the DOM for
 *      assistive tech and in the hover title (`compact` is passed to
 *      `renderGroup`, which draws the label as screen-reader text);
 *  (c) if even compact chips overflow, the line scrolls and an expand button
 *      lays the full list over the rows (the long-text box's pattern).
 */
export function GroupedChips<T>({
  groups,
  renderGroup,
}: {
  groups: ValueGroup<T>[]
  renderGroup: (group: ValueGroup<T>, badge: ReactNode, compact: boolean) => ReactNode
}) {
  const ref = useRef<HTMLSpanElement>(null)
  const [compact, setCompact] = useState(false)
  const [overflowing, setOverflowing] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const key = groups.map((g) => `${g.key}:${g.count}`).join('|')

  // Measure → compact → measure again. Widening the column (a resize) starts
  // over from full labels, so a cell that now fits shows them again.
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const over = el.scrollWidth > el.clientWidth + 1
    if (!compact) {
      if (over) setCompact(true)
      else setOverflowing(false)
    } else {
      setOverflowing(over)
    }
  }, [compact, key])
  // The ONE resize primitive (shared-primitives guard): a new cell width
  // starts over from full labels.
  const { ref: measuredRef, W } = useMeasuredCanvas()
  useEffect(() => {
    setCompact(false)
    setOverflowing(false)
  }, [W])

  if (groups.length === 0) return <span className="text-muted-foreground">—</span>

  const draw = (asCompact: boolean) =>
    groups.map((group) => {
      const badge = group.count > 1
        ? <span data-testid="group-count" className="shrink-0 pl-1 text-[11px] font-medium text-muted-foreground">×{group.count}</span>
        : null
      const sampleNames = (group.samples ?? []).map((s) => s.kenning).filter(Boolean)
      const title = `${group.label}${group.count > 1 ? ` ×${group.count}` : ''}`
        + (sampleNames.length ? ` — ${sampleNames.join(', ')}${group.count > sampleNames.length ? ', …' : ''}` : '')
      return (
        <span key={group.key} data-testid="set-group" title={title} className="inline-flex shrink-0 items-center">
          {renderGroup(group, badge, asCompact)}
        </span>
      )
    })

  const total = groups.reduce((sum, g) => sum + g.count, 0)
  return (
    <div ref={measuredRef} className="relative flex w-full min-w-0 items-center gap-1">
      <span
        ref={ref}
        data-testid="set-groups"
        data-compact={compact ? 'true' : 'false'}
        className={`flex min-w-0 flex-1 items-center gap-1 ${overflowing ? 'overflow-x-auto' : 'overflow-hidden'}`}
      >
        {draw(compact)}
      </span>
      {compact && overflowing && (
        <button
          type="button"
          data-testid="set-expand"
          aria-label={`Show all ${groups.length} groups (${total} values)`}
          aria-expanded={expanded}
          title={`Show all ${groups.length} groups (${total} values)`}
          onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v) }}
          className="shrink-0 rounded px-1 text-xs text-muted-foreground hover:bg-accent"
        >
          ⤢
        </button>
      )}
      {expanded && (
        <span
          data-testid="set-expanded"
          role="dialog"
          aria-label={`All ${groups.length} groups`}
          tabIndex={-1}
          ref={(el) => el?.focus()}
          onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setExpanded(false) } }}
          onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setExpanded(false) }}
          className="absolute left-0 top-0 z-30 flex max-h-[50vh] w-max min-w-full max-w-[32rem] flex-wrap gap-1 overflow-auto rounded-sm bg-background p-1.5 shadow-float outline-none ring-2 ring-ring"
        >
          {draw(false)}
        </span>
      )}
    </div>
  )
}
